import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'baseui/snackbar'

import { breakManagementActions } from '../breakManagementSagas'
import { selectBreakManagement } from '../breakManagementSelectors'
import { setExportRange } from '../breakManagementSlice'
import { fromIsoDate, toIsoDate } from '../helpers/toIsoDate'
import { ExportRange } from '../types'

const lastAllowedEnd = (start: string): string => {
  const date = fromIsoDate(start)
  date.setFullYear(date.getFullYear() + 1)

  return toIsoDate(date)
}

interface DatePickerChange {
  date: Date | Date[] | null | undefined
}

const useBreakExportForm = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { enqueue } = useSnackbar()
  const { exportRange, isExporting } = useSelector(selectBreakManagement)
  const today = toIsoDate(new Date())
  const start = exportRange.start ?? today
  const end = exportRange.end ?? today

  const rangeTooLong = end > lastAllowedEnd(start)
  const canSubmit = !isExporting && start <= end && !rangeTooLong

  const changeDate = (field: keyof ExportRange) => ({ date }: DatePickerChange) => {
    if (!(date instanceof Date)) return

    dispatch(setExportRange({ [field]: toIsoDate(date) }))
  }

  const submit = () => {
    if (!canSubmit) return

    dispatch({ type: breakManagementActions.EXPORT, start, end, enqueue })
  }

  return {
    t,
    startDate: fromIsoDate(start),
    endDate: fromIsoDate(end),
    onStartDateChange: changeDate('start'),
    onEndDateChange: changeDate('end'),
    rangeError: rangeTooLong ? t('breakManagement:export.range_too_long') : null,
    canSubmit,
    submit,
  }
}

export default useBreakExportForm
